import { Gauge } from 'lucide-react';
import { CEFR_LEVELS, type LevelAssessmentView } from '@en3/shared';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/cn';

/**
 * 水平仪表盘：CEFR 等级 + 置信度 + 词汇量区间 + 综合得分。
 *
 * 三个刻意的设计：
 *
 * 1. **置信度必须与等级同屏**。只给一个「B1」会让用户当成体检报告上的确定结论，
 *    而实际它只是从查词密度、复习保持率等行为特征推出来的估计。低于 60% 时
 *    直接写出"数据还少，仅供参考"，并告诉用户怎么做能变准（多读几篇、复习几次）。
 *    阈值与 `shared/lib/level` 里生成 `summaryText` 用的 0.6 保持一致 ——
 *    两处不一致会出现"卡片说仅供参考、文字说数据充分"的自相矛盾。
 *
 * 2. **词汇量给区间而不是单点**。真实词汇量无法被精确测量，显示"4312 词"是伪精确；
 *    区间（中位估计 ± spread，由 shared 计算）才是这个算法真正能支撑的结论。
 *
 * 3. **档位条画全 6 档**，当前档高亮、低于当前档的做弱化处理。用户因此能一眼看出
 *    "我现在在哪、离下一档还有多远"，而不是只知道一个孤立的字母组合。
 */

/** 与 `shared/lib/level` 保持一致：低于它就明确说"仅供参考"。 */
const LOW_CONFIDENCE = 0.6;

function formatNumber(value: number): string {
  return value.toLocaleString('zh-CN');
}

function formatDateTime(epochMs: number): string {
  if (!Number.isFinite(epochMs) || epochMs <= 0) return '—';
  return new Date(epochMs).toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LevelGauge({ assessment, className }: { assessment: LevelAssessmentView; className?: string }) {
  const { cefr, cefrConfidence, vocabEstimate, vocabMin, vocabMax, score, modelVersion, createdAt } = assessment;

  const confidencePct = Math.round(Math.max(0, Math.min(1, cefrConfidence)) * 100);
  const lowConfidence = cefrConfidence < LOW_CONFIDENCE;
  const currentIndex = CEFR_LEVELS.indexOf(cefr);

  // 区间缺一头（老数据）时退回中位估计，避免出现 "2,000 – 词" 这种半截文案
  const rangeMin = vocabMin ?? vocabEstimate;
  const rangeMax = vocabMax ?? vocabEstimate;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge aria-hidden className="size-4 text-primary" />
          当前水平
        </CardTitle>
        <Badge tone="muted" title="估算算法版本，改算法后版本号会变，便于对比历史结论">
          模型 {modelVersion}
        </Badge>
      </CardHeader>

      <CardBody className="space-y-5 text-sm">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
          <div>
            <p className="text-xs text-muted-foreground">CEFR 等级</p>
            <p className="mt-0.5 text-5xl leading-none font-bold tracking-tight text-primary">{cefr}</p>
          </div>

          <div className="min-w-[9rem] flex-1">
            <p className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
              <span>置信度</span>
              <span className="font-semibold tabular-nums text-foreground">{confidencePct}%</span>
            </p>
            <div
              className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={confidencePct}
              aria-label="评估置信度"
            >
              <div
                className={cn('h-full rounded-full', lowConfidence ? 'bg-[var(--warning)]' : 'bg-primary')}
                style={{ width: `${confidencePct}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {lowConfidence
                ? '数据还少，仅供参考：多读 2–3 篇并复习几次后再来评估会明显更准。'
                : '数据量已比较充分，结论相对稳定。'}
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground">综合得分</p>
            <p className="mt-0.5 text-3xl leading-none font-semibold tabular-nums">
              {score === null ? '—' : Math.round(score)}
              <span className="ml-1 text-sm font-normal text-muted-foreground">/ 100</span>
            </p>
          </div>
        </div>

        {/* 档位条：把"我在哪一档"变成位置信息，而不是一个孤立的字母 */}
        <div>
          <p className="text-xs text-muted-foreground">CEFR 档位（A1 → C2）</p>
          <ol className="mt-1.5 grid grid-cols-6 gap-1">
            {CEFR_LEVELS.map((level, index) => {
              const isCurrent = level === cefr;
              const isPassed = index < currentIndex;
              return (
                <li
                  key={level}
                  aria-current={isCurrent ? 'true' : undefined}
                  className={cn(
                    'rounded-md border py-1.5 text-center text-xs tabular-nums',
                    isCurrent
                      ? 'border-primary bg-accent font-semibold text-accent-foreground'
                      : isPassed
                        ? 'bg-muted text-muted-foreground'
                        : 'border-dashed text-muted-foreground',
                  )}
                >
                  {level}
                </li>
              );
            })}
          </ol>
        </div>

        <div>
          <p className="text-xs text-muted-foreground">词汇量（区间估计）</p>
          <p className="mt-0.5 text-lg font-semibold tabular-nums">
            {formatNumber(rangeMin)} – {formatNumber(rangeMax)}
            <span className="ml-1 text-xs font-normal text-muted-foreground">词</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            中位估计 {formatNumber(vocabEstimate)} 词。给区间而不是单点，是因为词汇量无法被精确测量 ——
            任何精确到个位的数字都是假的。
          </p>
        </div>

        <p className="text-xs text-muted-foreground">
          本次评估时间：{formatDateTime(createdAt)}
          {assessment.articleId ? '（基于单篇文章的学习记录）' : '（基于全部学习记录）'}
        </p>
      </CardBody>
    </Card>
  );
}
